import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  CheckCircle2,
  Clock3,
  ExternalLink,
  FileText,
  Filter,
  GraduationCap,
  HeartHandshake,
  Link2,
  PlayCircle,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { csrfFetch } from '../contexts/csrfFetch';
import { useSearchParams } from 'react-router-dom';

const API_URL = '/.netlify/functions/api/student-explanations';
const ADMIN_API_URL = '/.netlify/functions/api/admin/student-explanations';

const TYPE_OPTIONS = [
  { value: '', label: 'الكل' },
  { value: 'video', label: 'شرح مرئي' },
  { value: 'document', label: 'ملف تعليمي' },
  { value: 'summary', label: 'ملخص' },
  { value: 'practice', label: 'تدريبات' },
  { value: 'other', label: 'مصدر آخر' },
];

const TYPE_META = {
  video: { label: 'شرح مرئي', icon: PlayCircle, color: 'text-rose-200 bg-rose-400/10 border-rose-300/20' },
  document: { label: 'ملف تعليمي', icon: FileText, color: 'text-sky-200 bg-sky-400/10 border-sky-300/20' },
  summary: { label: 'ملخص', icon: BookOpen, color: 'text-amber-200 bg-amber-400/10 border-amber-300/20' },
  practice: { label: 'تدريبات', icon: GraduationCap, color: 'text-violet-200 bg-violet-400/10 border-violet-300/20' },
  other: { label: 'مصدر آخر', icon: Link2, color: 'text-teal-200 bg-teal-400/10 border-teal-300/20' },
};

const STATUS_META = {
  pending: { label: 'قيد المراجعة', className: 'border-amber-300/20 bg-amber-300/10 text-amber-100' },
  approved: { label: 'منشور', className: 'border-emerald-300/20 bg-emerald-300/10 text-emerald-100' },
  rejected: { label: 'مرفوض', className: 'border-rose-300/20 bg-rose-300/10 text-rose-100' },
};

const INITIAL_FORM = {
  contributorName: '',
  title: '',
  courseName: '',
  courseCode: '',
  contentType: 'video',
  academicLevel: '',
  resourceUrl: '',
  description: '',
  tags: '',
};

const requestJSON = async (url, options = {}) => {
  const response = await csrfFetch(url, {
    credentials: 'include',
    ...options,
    headers: {
      Accept: 'application/json',
      ...(options.headers || {}),
    },
  });
  const text = await response.text();
  let payload = {};
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    payload = {};
  }
  if (!response.ok) {
    throw new Error(payload.message || payload.error || `HTTP ${response.status}`);
  }
  return payload;
};

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('ar-SA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(value));
  } catch {
    return '';
  }
};

const StudentExplanationsPage = () => {
  const { isAuthenticated, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ explanations: 0, contributors: 0, courses: 0 });
  const [pendingItems, setPendingItems] = useState([]);
  const [myItems, setMyItems] = useState([]);
  const [loadingMine, setLoadingMine] = useState(false);
  const [search, setSearch] = useState('');
  const [activeType, setActiveType] = useState('');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [moderatingId, setModeratingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [message, setMessage] = useState(null);

  const role = useMemo(() => String(
    user?.role || user?.app_metadata?.role || user?.user_metadata?.role || ''
  ).toLowerCase(), [user]);
  const isAdmin = isAuthenticated && ['owner', 'developer', 'admin'].includes(role);

  const loadExplanations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('q', search.trim());
      if (activeType) params.set('type', activeType);
      const payload = await requestJSON(`${API_URL}?${params.toString()}`);
      setItems(Array.isArray(payload.explanations) ? payload.explanations : []);
      setStats(payload.stats || { explanations: 0, contributors: 0, courses: 0 });
    } catch (error) {
      setMessage({ type: 'error', text: 'تعذّر تحميل الشروحات حاليًا. حاول مرة أخرى.' });
    } finally {
      setLoading(false);
    }
  }, [activeType, search]);

  const loadPending = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const payload = await requestJSON(ADMIN_API_URL);
      setPendingItems(Array.isArray(payload.explanations) ? payload.explanations : []);
    } catch {
      setPendingItems([]);
    }
  }, [isAdmin]);

  const loadMine = useCallback(async () => {
    if (!isAuthenticated) {
      setMyItems([]);
      return;
    }
    setLoadingMine(true);
    try {
      const payload = await requestJSON(`${API_URL}/mine`);
      setMyItems(Array.isArray(payload.explanations) ? payload.explanations : []);
    } catch {
      setMyItems([]);
    } finally {
      setLoadingMine(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const timer = window.setTimeout(loadExplanations, 250);
    return () => window.clearTimeout(timer);
  }, [loadExplanations]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  useEffect(() => {
    const explanationId = searchParams.get('explanation');
    if (!explanationId || loading || loadingMine) return;
    const target = Array.from(document.querySelectorAll('[data-explanation-id]'))
      .find((element) => element.dataset.explanationId === explanationId);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('ring-2', 'ring-[#28e3da]', 'ring-offset-4', 'ring-offset-[#071522]');
    const timer = window.setTimeout(() => {
      target.classList.remove('ring-2', 'ring-[#28e3da]', 'ring-offset-4', 'ring-offset-[#071522]');
    }, 3200);
    return () => window.clearTimeout(timer);
  }, [items, loading, loadingMine, myItems, searchParams]);

  useEffect(() => {
    if (!user || form.contributorName) return;
    const suggestedName =
      user?.user_metadata?.full_name ||
      user?.user_metadata?.name ||
      user?.display_name ||
      user?.username ||
      user?.email?.split('@')[0] ||
      '';
    setForm((current) => ({ ...current, contributorName: suggestedName }));
  }, [form.contributorName, user]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage(null);
    if (!isAuthenticated) {
      setMessage({ type: 'error', text: 'سجّل الدخول أولًا حتى ترسل شرحك للمراجعة.' });
      return;
    }
    setSubmitting(true);
    try {
      await requestJSON(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          tags: form.tags.split(/[,،]/).map((tag) => tag.trim()).filter(Boolean),
        }),
      });
      setForm((current) => ({ ...INITIAL_FORM, contributorName: current.contributorName }));
      setMessage({
        type: 'success',
        text: 'وصل شرحك بنجاح. سيظهر في المكتبة بعد مراجعته واعتماده.',
      });
      document.getElementById('contribute')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      loadMine();
      if (isAdmin) loadPending();
    } catch (error) {
      const duplicate = error.message === 'submission_already_exists';
      setMessage({
        type: 'error',
        text: duplicate
          ? 'سبق أن أرسلت شرحًا بالعنوان نفسه لهذا المقرر.'
          : 'تعذّر إرسال المساهمة. تأكد من البيانات والرابط ثم حاول مجددًا.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const moderate = async (id, status) => {
    setModeratingId(id);
    try {
      await requestJSON(`${ADMIN_API_URL}/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      setPendingItems((current) => current.filter((item) => item.id !== id));
      setMessage({
        type: 'success',
        text: status === 'approved' ? 'تم اعتماد الشرح ونشره.' : 'تم رفض المساهمة.',
      });
      if (status === 'approved') loadExplanations();
    } catch {
      setMessage({ type: 'error', text: 'تعذّر تحديث حالة المساهمة.' });
    } finally {
      setModeratingId(null);
    }
  };

  const deleteExplanation = async () => {
    if (!deleteTarget?.id) return;
    const id = deleteTarget.id;
    setDeletingId(id);
    try {
      await requestJSON(`${API_URL}/${encodeURIComponent(id)}`, { method: 'DELETE' });
      setItems((current) => current.filter((item) => item.id !== id));
      setMyItems((current) => current.filter((item) => item.id !== id));
      setPendingItems((current) => current.filter((item) => item.id !== id));
      setDeleteTarget(null);
      setMessage({ type: 'success', text: 'تم حذف الشرح نهائيًا.' });
      loadExplanations();
    } catch {
      setMessage({ type: 'error', text: 'تعذّر حذف الشرح، أو أنك لا تملك صلاحية حذفه.' });
    } finally {
      setDeletingId(null);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contribute')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div dir="rtl" className="relative mx-auto w-full max-w-[1280px] px-4 pb-20 pt-5 font-['Alexandria'] md:px-8 md:pt-2">
      <section className="relative overflow-hidden rounded-[28px] border border-[#28e3da33] bg-[#0b1a2a]/90 px-5 py-8 shadow-[0_24px_80px_rgba(0,0,0,0.24)] md:px-10 md:py-12">
        <div className="pointer-events-none absolute -left-16 -top-24 h-72 w-72 rounded-full bg-[#28e3da20] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 right-1/3 h-64 w-64 rounded-full bg-[#6b8cff16] blur-3xl" />
        <div className="relative grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_360px] xl:grid-cols-[minmax(0,1fr)_400px]">
          <div className="min-w-0">
            <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#28e3da40] bg-[#28e3da12] px-3 py-1.5 text-xs font-semibold text-[#7cf6ef]">
              <Sparkles size={15} />
              معرفة من طالب إلى طالب
            </span>
            <h1
              className="max-w-full text-[clamp(1.1rem,2.6vw,1.85rem)] font-bold text-white"
              style={{ lineHeight: 1.5, letterSpacing: '-0.025em' }}
            >
              شرحك البسيط قد يصنع <span className="text-[#28e3da]">فرقًا حقيقيًا</span> لطالب آخر
            </h1>
            <p className="mt-5 max-w-2xl text-sm text-slate-300 md:text-base" style={{ lineHeight: 2 }}>
              مساحة تجمع أفضل الشروحات والملخصات والتدريبات التي يصنعها طلاب الجامعة،
              <span className="md:block"> بطريقة منظمة وآمنة وسهلة الوصول.</span>
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={scrollToForm}
                className="inline-flex items-center gap-2 rounded-xl bg-[#28e3da] px-5 py-3 text-sm font-bold text-[#07131f] transition hover:-translate-y-0.5 hover:bg-[#70fff8]"
              >
                <HeartHandshake size={19} />
                شارك شرحك
              </button>
              <button
                type="button"
                onClick={() => document.getElementById('library')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition hover:border-[#28e3da80] hover:bg-[#28e3da10]"
              >
                <BookOpen size={18} />
                تصفح المكتبة
              </button>
            </div>
          </div>

          <div className="grid w-full grid-cols-3 gap-2 rounded-2xl border border-white/10 bg-white/[0.035] p-3 backdrop-blur md:gap-3 md:p-4 lg:grid-cols-1">
            {[
              { label: 'شرح معتمد', value: stats.explanations, icon: BookOpen },
              { label: 'طالب مساهم', value: stats.contributors, icon: Users },
              { label: 'مقرر', value: stats.courses, icon: GraduationCap },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="flex flex-col items-center gap-2 rounded-xl border border-white/[0.07] bg-[#08142399] px-2 py-4 text-center lg:grid lg:grid-cols-[minmax(0,1fr)_40px_24px] lg:gap-3 lg:px-5 lg:text-right">
                <span className="order-3 whitespace-nowrap text-[10px] text-slate-400 md:text-xs lg:order-none">{label}</span>
                <span className="order-1 w-10 text-center text-xl font-bold tabular-nums text-white md:text-2xl lg:order-none">
                  {value}
                </span>
                <span className="order-2 flex h-6 w-6 items-center justify-center lg:order-none">
                  <Icon size={20} strokeWidth={1.8} className="text-[#28e3da]" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {message && (
        <div className={`mt-5 flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${
          message.type === 'success'
            ? 'border-emerald-400/25 bg-emerald-400/10 text-emerald-100'
            : 'border-rose-400/25 bg-rose-400/10 text-rose-100'
        }`}>
          {message.type === 'success' ? <CheckCircle2 size={19} /> : <ShieldCheck size={19} />}
          <span>{message.text}</span>
        </div>
      )}
      {isAuthenticated && (
        <section className="pt-14">
          <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
            <div>
              <span className="text-xs font-semibold text-[#28e3da]">لوحة المساهم</span>
              <h2 className="mt-2 text-2xl font-bold text-white">شروحاتي ومساهماتي</h2>
              <p className="mt-2 text-xs leading-6 text-slate-400">تابع حالة كل شرح واحذفه في أي وقت، سواء كان قيد المراجعة أو منشورًا.</p>
            </div>
            <span className="w-fit rounded-full border border-white/10 bg-white/[0.035] px-3 py-1.5 text-xs text-slate-300">
              {myItems.length} مساهمة
            </span>
          </div>
          {loadingMine ? (
            <div className="h-28 animate-pulse rounded-2xl border border-white/5 bg-white/[0.035]" />
          ) : myItems.length ? (
            <div className="space-y-3">
              {myItems.map((item) => {
                const statusMeta = STATUS_META[item.status] || STATUS_META.pending;
                return (
                  <article key={item.id} data-explanation-id={item.id} className="rounded-2xl border border-white/10 bg-[#0b1a2a]/95 p-5 transition">
                    <div className="flex flex-col justify-between gap-5 md:flex-row md:items-center">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${statusMeta.className}`}>
                            {statusMeta.label}
                          </span>
                          <span className="text-[11px] text-slate-500">{formatDate(item.createdAt)}</span>
                        </div>
                        <h3 className="mt-3 font-bold text-white">{item.title}</h3>
                        <p className="mt-1 text-xs text-slate-400">{item.courseName}{item.courseCode ? ` · ${item.courseCode}` : ''}</p>
                        {item.reviewNote && <p className="mt-2 text-xs leading-6 text-rose-200">ملاحظة المراجعة: {item.reviewNote}</p>}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <a
                          href={item.resourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-xl border border-[#28e3da45] px-4 py-2.5 text-xs font-semibold text-[#7cf6ef]"
                        >
                          فتح <ExternalLink size={13} />
                        </a>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(item)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-rose-300/20 bg-rose-400/10 px-4 py-2.5 text-xs font-semibold text-rose-100 transition hover:bg-rose-400/20"
                        >
                          <Trash2 size={14} />
                          حذف
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-white/10 bg-[#0b1a2a]/70 px-5 py-8 text-center text-sm text-slate-400">
              لم ترسل أي شرح حتى الآن.
            </div>
          )}
        </section>
      )}
      <section id="library" className="scroll-mt-24 pt-14">
        <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <span className="text-xs font-semibold text-[#28e3da]">مكتبة الطلاب</span>
            <h2 className="mt-2 text-2xl font-bold text-white md:text-3xl">ابحث عن الشرح الذي تحتاجه</h2>
            <p className="mt-2 text-sm text-slate-400">كل مصدر ظاهر هنا تمت مراجعته قبل النشر.</p>
          </div>
          <div className="relative w-full md:max-w-md">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="ابحث باسم المقرر أو عنوان الشرح..."
              className="w-full rounded-2xl border border-white/10 bg-[#0b1a2a]/90 py-3.5 pl-4 pr-11 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-[#28e3da80]"
            />
          </div>
        </div>

        <div className="mb-7 flex items-center gap-2 overflow-x-auto pb-2">
          <Filter size={17} className="ml-1 shrink-0 text-slate-500" />
          {TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setActiveType(option.value)}
              className={`shrink-0 rounded-full border px-4 py-2 text-xs font-semibold transition ${
                activeType === option.value
                  ? 'border-[#28e3da] bg-[#28e3da] text-[#07131f]'
                  : 'border-white/10 bg-white/[0.035] text-slate-300 hover:border-[#28e3da60]'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map((key) => (
              <div key={key} className="h-72 animate-pulse rounded-2xl border border-white/5 bg-white/[0.035]" />
            ))}
          </div>
        ) : items.length ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((item) => {
              const meta = TYPE_META[item.contentType] || TYPE_META.other;
              const TypeIcon = meta.icon;
              return (
                <article key={item.id} data-explanation-id={item.id} className="group flex min-h-[290px] flex-col rounded-2xl border border-white/10 bg-[#0b1a2a]/90 p-5 transition duration-300 hover:-translate-y-1 hover:border-[#28e3da55] hover:shadow-[0_18px_55px_rgba(0,0,0,0.22)]">
                  <div className="flex items-center justify-between gap-3">
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${meta.color}`}>
                      <TypeIcon size={14} />
                      {meta.label}
                    </span>
                    <span className="text-[11px] text-slate-500">{formatDate(item.createdAt)}</span>
                  </div>
                  <div className="mt-5">
                    <p className="text-xs font-semibold text-[#28e3da]">
                      {item.courseName}{item.courseCode ? ` · ${item.courseCode}` : ''}
                    </p>
                    <h3 className="mt-2 text-lg font-bold leading-7 text-white">{item.title}</h3>
                    <p className="mt-3 line-clamp-3 text-xs leading-6 text-slate-400">{item.description}</p>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {(item.tags || []).map((tag) => (
                      <span key={tag} className="rounded-md bg-white/5 px-2 py-1 text-[10px] text-slate-400">#{tag}</span>
                    ))}
                  </div>
                  <div className="mt-auto flex items-center justify-between border-t border-white/[0.07] pt-4">
                    <span className="flex items-center gap-2 text-xs text-slate-300">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#28e3da16] font-bold text-[#28e3da]">
                        {item.contributorName?.trim()?.[0] || 'ط'}
                      </span>
                      {item.contributorName}
                    </span>
                    <div className="flex items-center gap-2">
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(item)}
                          aria-label={`حذف ${item.title}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-300/20 text-rose-200 transition hover:bg-rose-400/10"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                      <a
                        href={item.resourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#28e3da45] px-3 py-2 text-xs font-semibold text-[#7cf6ef] transition hover:bg-[#28e3da12]"
                      >
                        افتح الشرح
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="rounded-3xl border border-dashed border-[#28e3da35] bg-[#0b1a2a]/70 px-6 py-16 text-center">
            <BookOpen className="mx-auto text-[#28e3da]" size={38} />
            <h3 className="mt-4 text-lg font-bold text-white">لا توجد شروحات مطابقة حتى الآن</h3>
            <p className="mt-2 text-sm text-slate-400">جرّب بحثًا آخر، أو كن أول من يشارك شرحًا لهذا المقرر.</p>
            <button type="button" onClick={scrollToForm} className="mt-5 text-sm font-semibold text-[#28e3da] hover:underline">
              أضف أول شرح
            </button>
          </div>
        )}
      </section>



      <section id="contribute" className="scroll-mt-20 pt-16">
        <div className="grid overflow-hidden rounded-[28px] border border-[#28e3da30] bg-[#0b1a2a]/95 shadow-[0_24px_70px_rgba(0,0,0,0.2)] lg:grid-cols-[.72fr_1.28fr]">
          <div className="relative overflow-hidden border-b border-white/10 bg-[#28e3da0b] p-6 md:p-8 lg:border-b-0 lg:border-l">
            <div className="absolute -left-20 -top-20 h-52 w-52 rounded-full bg-[#28e3da18] blur-3xl" />
            <div className="relative">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-[#28e3da40] bg-[#28e3da14] text-[#28e3da]">
                <Send size={22} />
              </span>
              <h2 className="mt-6 text-2xl font-bold leading-10 text-white">حوّل مجهودك إلى أثر</h2>
              <p className="mt-3 text-sm leading-7 text-slate-400">
                شارك رابط شرحك أو ملخصك أو تدريباتك. نراجع المحتوى ثم ننشره باسمك ليستفيد منه الطلاب.
              </p>
              <div className="mt-7 space-y-4">
                {[
                  'اكتب عنوانًا واضحًا ومحددًا',
                  'أضف وصفًا يوضح ما سيستفيده الطالب',
                  'تأكد أن الرابط يعمل ومتاح للمشاهدة',
                  'لا ترفع محتوى لا تملك حق مشاركته',
                ].map((text, index) => (
                  <div key={text} className="flex items-start gap-3 text-xs leading-6 text-slate-300">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#28e3da14] text-[10px] font-bold text-[#28e3da]">
                      {index + 1}
                    </span>
                    {text}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            <div className="mb-6 flex items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-white">بيانات المساهمة</h3>
                <p className="mt-1 text-xs text-slate-500">جميع الحقول الأساسية مطلوبة.</p>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/20 bg-amber-300/10 px-3 py-1.5 text-[10px] text-amber-100">
                <Clock3 size={13} />
                تخضع للمراجعة
              </span>
            </div>

            {!isAuthenticated && (
              <div className="mb-5 rounded-xl border border-amber-300/20 bg-amber-300/10 px-4 py-3 text-xs leading-6 text-amber-100">
                يمكنك تعبئة النموذج، لكن يلزم تسجيل الدخول قبل الإرسال.
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-2 text-xs text-slate-300">
                <span>اسم المساهم</span>
                <input
                  value={form.contributorName}
                  onChange={(event) => setForm({ ...form, contributorName: event.target.value })}
                  maxLength={80}
                  placeholder="الاسم الذي سيظهر للطلاب"
                  className="w-full rounded-xl border border-white/10 bg-[#071522] px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-[#28e3da70]"
                />
              </label>
              <label className="space-y-2 text-xs text-slate-300">
                <span>نوع المحتوى *</span>
                <select
                  required
                  value={form.contentType}
                  onChange={(event) => setForm({ ...form, contentType: event.target.value })}
                  className="w-full rounded-xl border border-white/10 bg-[#071522] px-4 py-3 text-sm text-white outline-none transition focus:border-[#28e3da70]"
                >
                  {TYPE_OPTIONS.filter((option) => option.value).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="space-y-2 text-xs text-slate-300 md:col-span-2">
                <span>عنوان الشرح *</span>
                <input
                  required
                  minLength={5}
                  maxLength={120}
                  value={form.title}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                  placeholder="مثال: شرح مبسط للفصل الثالث مع أهم الأفكار"
                  className="w-full rounded-xl border border-white/10 bg-[#071522] px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-[#28e3da70]"
                />
              </label>
              <label className="space-y-2 text-xs text-slate-300">
                <span>اسم المقرر *</span>
                <input
                  required
                  minLength={2}
                  maxLength={100}
                  value={form.courseName}
                  onChange={(event) => setForm({ ...form, courseName: event.target.value })}
                  placeholder="مثل: مبادئ المحاسبة"
                  className="w-full rounded-xl border border-white/10 bg-[#071522] px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-[#28e3da70]"
                />
              </label>
              <label className="space-y-2 text-xs text-slate-300">
                <span>رمز المقرر</span>
                <input
                  maxLength={30}
                  value={form.courseCode}
                  onChange={(event) => setForm({ ...form, courseCode: event.target.value })}
                  placeholder="ACC 101"
                  dir="ltr"
                  className="w-full rounded-xl border border-white/10 bg-[#071522] px-4 py-3 text-right text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-[#28e3da70]"
                />
              </label>
              <label className="space-y-2 text-xs text-slate-300">
                <span>المستوى الدراسي</span>
                <input
                  maxLength={50}
                  value={form.academicLevel}
                  onChange={(event) => setForm({ ...form, academicLevel: event.target.value })}
                  placeholder="مثال: المستوى الرابع"
                  className="w-full rounded-xl border border-white/10 bg-[#071522] px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-[#28e3da70]"
                />
              </label>
              <label className="space-y-2 text-xs text-slate-300">
                <span>الكلمات المفتاحية</span>
                <input
                  value={form.tags}
                  onChange={(event) => setForm({ ...form, tags: event.target.value })}
                  placeholder="اختبار، مراجعة، فصل ثالث"
                  className="w-full rounded-xl border border-white/10 bg-[#071522] px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-[#28e3da70]"
                />
              </label>
              <label className="space-y-2 text-xs text-slate-300 md:col-span-2">
                <span>رابط الشرح أو الملف *</span>
                <input
                  required
                  type="url"
                  value={form.resourceUrl}
                  onChange={(event) => setForm({ ...form, resourceUrl: event.target.value })}
                  placeholder="https://..."
                  dir="ltr"
                  className="w-full rounded-xl border border-white/10 bg-[#071522] px-4 py-3 text-left text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-[#28e3da70]"
                />
              </label>
              <label className="space-y-2 text-xs text-slate-300 md:col-span-2">
                <span>وصف المحتوى وما الذي سيستفيده الطالب *</span>
                <textarea
                  required
                  minLength={20}
                  maxLength={1200}
                  rows={5}
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                  placeholder="اكتب وصفًا مختصرًا وواضحًا لمحتوى الشرح..."
                  className="w-full resize-none rounded-xl border border-white/10 bg-[#071522] px-4 py-3 text-sm leading-7 text-white outline-none transition placeholder:text-slate-600 focus:border-[#28e3da70]"
                />
                <span className="block text-left text-[10px] text-slate-600">{form.description.length}/1200</span>
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#28e3da] px-5 py-3.5 text-sm font-bold text-[#07131f] transition hover:bg-[#70fff8] disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
            >
              {submitting ? <Clock3 className="animate-spin" size={18} /> : <Send size={18} />}
              {submitting ? 'جاري الإرسال...' : 'إرسال للمراجعة'}
            </button>
          </form>
        </div>
      </section>

      {isAdmin && pendingItems.length > 0 && (
        <section className="pt-14">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-amber-200">للمشرفين فقط</span>
              <h2 className="mt-2 text-2xl font-bold text-white">مساهمات تنتظر المراجعة</h2>
            </div>
            <span className="rounded-full bg-amber-300/10 px-3 py-1 text-xs text-amber-100">{pendingItems.length}</span>
          </div>
          <div className="space-y-3">
            {pendingItems.map((item) => (
              <article key={item.id} className="rounded-2xl border border-amber-300/15 bg-[#0b1a2a]/95 p-5">
                <div className="flex flex-col justify-between gap-5 md:flex-row md:items-center">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                      <span>{item.contributorName}</span>
                      <span>•</span>
                      <span>{item.courseName}</span>
                      <span>•</span>
                      <span>{formatDate(item.createdAt)}</span>
                    </div>
                    <h3 className="mt-2 font-bold text-white">{item.title}</h3>
                    <p className="mt-2 line-clamp-2 text-xs leading-6 text-slate-400">{item.description}</p>
                    <a href={item.resourceUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-[#28e3da] hover:underline">
                      معاينة الرابط <ExternalLink size={13} />
                    </a>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      disabled={moderatingId === item.id}
                      onClick={() => moderate(item.id, 'approved')}
                      className="rounded-xl bg-emerald-400/15 px-4 py-2.5 text-xs font-semibold text-emerald-100 transition hover:bg-emerald-400/25 disabled:opacity-50"
                    >
                      اعتماد
                    </button>
                    <button
                      type="button"
                      disabled={moderatingId === item.id}
                      onClick={() => moderate(item.id, 'rejected')}
                      className="rounded-xl bg-rose-400/10 px-4 py-2.5 text-xs font-semibold text-rose-100 transition hover:bg-rose-400/20 disabled:opacity-50"
                    >
                      رفض
                    </button>
                    <button
                      type="button"
                      disabled={deletingId === item.id}
                      onClick={() => setDeleteTarget(item)}
                      aria-label={`حذف ${item.title}`}
                      className="inline-flex items-center justify-center rounded-xl border border-rose-300/20 px-3 py-2.5 text-rose-200 transition hover:bg-rose-400/10 disabled:opacity-50"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#020912]/80 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="delete-explanation-title">
          <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0b1a2a] p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="delete-explanation-title" className="text-lg font-bold text-white">حذف الشرح نهائيًا؟</h2>
                <p className="mt-2 text-xs leading-6 text-slate-400">
                  سيتم حذف «{deleteTarget.title}» ولن يظهر للطلاب بعد ذلك. لا يمكن التراجع عن هذه الخطوة.
                </p>
              </div>
              <button type="button" onClick={() => setDeleteTarget(null)} aria-label="إغلاق" className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                disabled={deletingId === deleteTarget.id}
                onClick={deleteExplanation}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-rose-500 px-4 py-3 text-sm font-bold text-white transition hover:bg-rose-400 disabled:opacity-50"
              >
                {deletingId === deleteTarget.id ? <Clock3 className="animate-spin" size={16} /> : <Trash2 size={16} />}
                {deletingId === deleteTarget.id ? 'جاري الحذف...' : 'نعم، احذف الشرح'}
              </button>
              <button type="button" disabled={deletingId === deleteTarget.id} onClick={() => setDeleteTarget(null)} className="rounded-xl border border-white/10 px-5 py-3 text-sm text-slate-300 hover:bg-white/5">
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentExplanationsPage;
